import { css } from '@emotion/react';
import {
  faCircleInfo, faScaleBalanced, faValueAbsolute,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  LottieAnimationComponent, LottieAnimationTypes,
} from '~/_shared/baseComponents/lottieAnimation';
import {
  ToggleComponent, ToggleSize,
} from '~/_shared/baseComponents/toggle';
import { OverlayLottieAnimationComponent } from '~/_shared/components/overlay/overlayLottieAnimation.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ChartItem } from './chartItem.types';
import { ChartItemVisualizer } from './chartItemVisualizer.component';

const containerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.secondary,
  border: `1px solid ${theme.borderColors.activeItem}`,
  borderRadius: 4,
  height: 300,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const headerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.highlight,
  color: theme.textColors.quaternary,
  height: 38,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 7px',
  gap: 20,
});

const titleStyle = css({
  fontSize: 20,
  fontWeight: 500,
  textTransform: 'uppercase',
});

const bottomPartStyle = css({
  padding: '0 10px 10px 10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
});

const statusBarStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: 8,
  height: 13,
  alignItems: 'center',
  gap: 4,
});

const statusBarItemStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: 13,
  height: 13,
  alignItems: 'center',
  gap: 2,
});

const righSideHeaderActionsContainerStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: 14,
  height: 14,
  fontSize: 14,
  alignItems: 'center',
  gap: 11,
});

const statusBarNoteItemStyle = (theme: Theme) => css({
  color: theme.textColors.link,
});

const statusBarDangerItemStyle = (theme: Theme) => css({
  color: theme.textColors.warning,
});

export const ChartItemComponent = (props: ChartItem) => {
  const {
    title,
    isSyncing,
    isLoading,
    isOutdated,
    shouldNormalize,
    onNormalizeToggleClick,
    metrics,
  } = props;

  const [t] = useTranslation();

  const isForceAbsoluteValue = metrics.barValues.length === 1;

  return (
    <div css={containerStyle}>
      <div css={headerStyle}>
        <div>
          <span css={titleStyle}>{title}</span>
        </div>
        <div css={righSideHeaderActionsContainerStyle} />
        {onNormalizeToggleClick && !isForceAbsoluteValue && (
          <ToggleComponent
            offIcon={faScaleBalanced}
            offIconTopOffset={1}
            onIcon={faValueAbsolute}
            onIconTopOffset={1}
            size={ToggleSize.M}
            isOn={!!shouldNormalize}
            onChange={(newValue: boolean) => onNormalizeToggleClick?.(newValue)}
          />
        )}
      </div>
      <div css={bottomPartStyle}>
        {isLoading && (
          <OverlayLottieAnimationComponent
            type={LottieAnimationTypes.BarGraph}
            size={120}
            autoplay
            loop
          />
        )}
        <div css={statusBarStyle}>
          {isOutdated && (
            <span css={[statusBarItemStyle, statusBarDangerItemStyle]}>
              <FontAwesomeIcon icon={faCircleInfo} />
              <span>{t('Potentially out of date')}</span>
            </span>
          )}
          {isSyncing && (
            <span css={[statusBarItemStyle, statusBarNoteItemStyle]}>
              <span>{t('Syncing')}</span>
              <span>
                <LottieAnimationComponent
                  type={LottieAnimationTypes.BarGraph}
                  size={7}
                  autoplay
                  loop
                />
              </span>
            </span>
          )}
        </div>
        <div>
          <ChartItemVisualizer
            {...props}
            shouldNormalize={isForceAbsoluteValue ? false : shouldNormalize}
          />
        </div>
      </div>
    </div>
  );
};
